/**
 * RBAC-3.6 · UX-DR11, FR-13 to FR-16. Review changes: what a save gains and loses, as consequences,
 * who it reaches, what the acting user personally loses, and a confirm carrying the count.
 *
 * The diff is `role_save_preview()`'s, not computed here: the save calls the same function, so the
 * dialog cannot show one diff and apply another. The save is bound to the count on the button and to
 * the permissions the editor started from; if either moved, the server refuses (22023) with its own
 * sentence, shown verbatim, and the dialog offers to review again.
 *
 * RBAC-8.1: with `restore`, the same dialog restores a version from the role's history. The preview is
 * `role_restore_preview()`, which IS `role_save_preview()` of the version's set, and the confirm is
 * `role_restore_version()`, which calls `role_save_permissions()`. So a restore shows the same lines,
 * the same count, the same personal callout and the same confirm as an ordinary save, and is refused
 * by the same guard rails. The one addition is a warning when the diff touches a reserved permission,
 * said before the confirm rather than discovered after it.
 */
import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Loader2, Minus, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  confirmLabel,
  diffLines,
  diffSubtitle,
  personalCallout,
  type DiffLine,
  type RoleSaveDiff,
} from '@/lib/rolePermissionDiff';
import { reservedWarning } from '@/lib/roleHistory';
import { previewRoleSave, saveRolePermissions } from '@/services/roleSaveService';
import { previewRoleRestore, restoreRoleVersion } from '@/services/roleHistoryService';

export interface ReviewChangesDialogProps {
  open: boolean;
  roleId: number;
  /** The role's grants when the editor opened it. */
  base: string[];
  /** The draft the editor would save. Ignored when restoring. */
  draft: string[];
  /** RBAC-8.1: restore this version instead of saving the draft. `when` names it in the title. */
  restore?: { versionId: number; when: string } | null;
  onOpenChange: (open: boolean) => void;
  onSaved: (result: RoleSaveDiff) => void;
}

function Section({ tone, heading, lines }: { tone: 'gain' | 'loss'; heading: string; lines: DiffLine[] }) {
  if (lines.length === 0) return null;
  const gain = tone === 'gain';
  const Icon = gain ? Plus : Minus;
  return (
    <section className="space-y-2">
      <h3
        className={
          gain
            ? 'flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-emerald-800 dark:text-emerald-300'
            : 'flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-red-700 dark:text-red-300'
        }
      >
        <Icon className="h-3.5 w-3.5" aria-hidden />
        {heading}
      </h3>
      <ul className="space-y-2">
        {lines.map((l) => (
          <li
            key={l.key}
            className={
              gain
                ? 'rounded-r-md border-l-4 border-emerald-500 bg-emerald-50 px-3 py-2 dark:bg-emerald-950/40'
                : 'rounded-r-md border-l-4 border-red-500 bg-red-50 px-3 py-2 dark:bg-red-950/40'
            }
          >
            <p className="text-sm font-medium text-foreground">{l.title}</p>
            <p className="text-sm text-muted-foreground">{l.sentence}</p>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function ReviewChangesDialog({ open, roleId, base, draft, restore, onOpenChange, onSaved }: ReviewChangesDialogProps) {
  const [preview, setPreview] = useState<RoleSaveDiff | null>(null);
  const [reserved, setReserved] = useState<string[]>([]);
  const versionId = restore?.versionId ?? null;
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setPreview(null);
    setReserved([]);
    try {
      if (versionId !== null) {
        const p = await previewRoleRestore(roleId, versionId);
        setPreview(p.diff);
        setReserved(p.reserved);
      } else {
        setPreview(await previewRoleSave(roleId, draft));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The changes could not be read.');
    } finally {
      setLoading(false);
    }
  }, [roleId, draft, versionId]);

  useEffect(() => {
    if (open) void load();
  }, [open, load]);

  const confirm = async () => {
    if (!preview) return;
    setSaving(true);
    setError(null);
    try {
      const saved =
        versionId !== null
          ? await restoreRoleVersion(roleId, versionId, base, preview)
          : await saveRolePermissions(roleId, draft, base, preview);
      onSaved(saved);
      onOpenChange(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The role could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  const lines = preview ? diffLines(preview) : null;
  const callout = preview ? personalCallout(preview) : null;
  const empty = preview && preview.gaining.length === 0 && preview.losing.length === 0;
  const warning = reservedWarning(reserved);

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (saving) return;
        onOpenChange(o);
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[720px]">
        <DialogHeader>
          <DialogTitle>{restore ? `Restore the version of ${restore.when}` : 'Review changes'}</DialogTitle>
          <DialogDescription>{preview ? diffSubtitle(preview) : 'Working out who this change reaches…'}</DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        )}

        {lines && (
          <div className="space-y-5">
            {empty && <p className="text-sm text-muted-foreground">Nothing this role can do would change, so there is nothing to save.</p>}
            {warning && (
              <p role="alert" className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
                {warning}
              </p>
            )}
            <Section tone="gain" heading="Gaining" lines={lines.gaining} />
            <Section tone="loss" heading="Losing" lines={lines.losing} />
            {callout && (
              <div className="flex gap-2.5 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-500/40 dark:bg-amber-950/40 dark:text-amber-100">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                <p>{callout}</p>
              </div>
            )}
          </div>
        )}

        {error && (
          <p role="alert" className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
            {error}
          </p>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Back
          </Button>
          {error && !loading ? (
            <Button onClick={() => void load()} disabled={saving}>
              Review again
            </Button>
          ) : (
            <Button onClick={() => void confirm()} disabled={!preview || !!empty || saving}>
              {saving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
              {preview ? confirmLabel(preview.holders.count) : 'Apply'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
